// Admin — Suppliers (list → drill-to-card)
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import PermissionGate from '../../components/PermissionGate';
import { Truck, Search, ArrowLeft, Edit3, Plus, ChevronRight, Package } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function AdminSuppliers() {
  const { token } = useAuth();
  const [list, setList] = useState([]);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const fetchList = async () => {
    setLoading(true);
    const params = new URLSearchParams({ active_only: true });
    if (search) params.append('search', search);
    const res = await fetch(`${API}/api/purchasing/suppliers?${params}`, { headers });
    const d = await res.json();
    setList(d.items || []);
    setLoading(false);
  };
  useEffect(() => { fetchList(); }, [search]);

  if (selected) return <SupplierCard id={selected.id} onBack={() => { setSelected(null); fetchList(); }} token={token} />;

  return (
    <div className="p-6 min-h-screen bg-uf-bg">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-3xl font-bold text-white">Suppliers</h1>
          <p className="text-uf-textMuted text-sm mt-1">{list.length} active suppliers</p>
        </div>
        <PermissionGate perm="purchasing.suppliers">
          <button onClick={() => setSelected({ id: 'new' })} data-testid="new-supplier-btn" className="px-3 py-2 bg-uf-red text-white rounded text-sm flex items-center gap-2"><Plus className="w-4 h-4" /> New Supplier</button>
        </PermissionGate>
      </div>

      <div className="relative mb-4">
        <Search className="w-4 h-4 absolute left-3 top-3 text-uf-textMuted" />
        <input placeholder="Search name or account number..." value={search} onChange={e => setSearch(e.target.value)} data-testid="supplier-search"
               className="w-full bg-uf-surface border border-uf-border pl-10 pr-4 py-2 text-white rounded" />
      </div>

      <div className="bg-uf-surface border border-uf-border rounded overflow-hidden">
        <div className="grid grid-cols-12 gap-2 px-4 py-3 bg-uf-bg text-uf-textMuted text-xs uppercase tracking-wider font-bold">
          <div className="col-span-4">Supplier</div>
          <div className="col-span-3">Contact</div>
          <div className="col-span-2">Terms</div>
          <div className="col-span-2">Account #</div>
          <div className="col-span-1"></div>
        </div>
        {loading && <div className="p-8 text-center text-uf-textMuted">Loading…</div>}
        {!loading && list.length === 0 && <div className="p-8 text-center text-uf-textMuted">No suppliers yet</div>}
        {list.map(s =>
          <div key={s.id} onClick={() => setSelected(s)} data-testid={`supplier-row-${s.id}`}
               className="grid grid-cols-12 gap-2 px-4 py-3 border-t border-uf-border hover:bg-uf-bg cursor-pointer items-center">
            <div className="col-span-4 text-white font-medium flex items-center gap-2"><Truck className="w-4 h-4 text-uf-textMuted" />{s.name}</div>
            <div className="col-span-3 text-uf-textMuted text-sm">{s.email || s.phone || '—'}</div>
            <div className="col-span-2 text-uf-textMuted text-sm">{s.payment_terms}</div>
            <div className="col-span-2 text-uf-textMuted text-xs font-mono">{s.account_number || '—'}</div>
            <div className="col-span-1 text-right"><ChevronRight className="w-4 h-4 text-uf-textMuted inline" /></div>
          </div>
        )}
      </div>
    </div>
  );
}

function SupplierCard({ id, onBack, token }) {
  const [tab, setTab] = useState('details');
  const [supplier, setSupplier] = useState(id === 'new' ? {} : null);
  const [editing, setEditing] = useState(id === 'new');
  const [form, setForm] = useState({});
  const [products, setProducts] = useState([]);
  const [pos, setPos] = useState([]);
  const [sites, setSites] = useState([]);
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  useEffect(() => {
    fetch(`${API}/api/sites`, { headers }).then(r => r.json()).then(d => setSites((d.items || d || []).filter(s => s.site_type !== 'warehouse')));
    if (id !== 'new') {
      fetch(`${API}/api/purchasing/suppliers/${id}`, { headers }).then(r => r.json()).then(s => { setSupplier(s); setForm(s); });
      fetch(`${API}/api/purchasing/suppliers/${id}/products`, { headers }).then(r => r.json()).then(d => setProducts(d.items || []));
      fetch(`${API}/api/purchasing/purchase-orders?supplier_id=${id}`, { headers }).then(r => r.json()).then(d => setPos(d.items || []));
    }
  }, [id]);

  const save = async () => {
    const body = id === 'new' ? form : { ...form, id };
    const res = await fetch(`${API}/api/purchasing/suppliers`, { method: 'POST', headers, body: JSON.stringify(body) });
    const updated = await res.json();
    if (res.ok) { setSupplier(updated); setForm(updated); setEditing(false); }
    else alert(updated.detail || 'Save failed');
  };

  if (!supplier) return <div className="p-6 text-white">Loading…</div>;

  return (
    <div className="p-6 min-h-screen bg-uf-bg">
      <button onClick={onBack} data-testid="supplier-card-back" className="flex items-center gap-2 text-uf-textMuted hover:text-white mb-4 text-sm"><ArrowLeft className="w-4 h-4" /> Back to suppliers</button>

      <div className="flex items-start justify-between mb-6">
        <div>
          <div className="text-uf-textMuted text-xs uppercase tracking-wider mb-1">Supplier</div>
          <h1 className="font-heading text-3xl font-bold text-white mb-1">{supplier.name || 'New Supplier'}</h1>
          <div className="text-uf-textMuted text-sm">{supplier.email}{supplier.phone && ` · ${supplier.phone}`}</div>
        </div>
        <div className="text-right">
          <div className="text-uf-textMuted text-xs uppercase tracking-wider mb-1">Payment terms</div>
          <div className="text-xl font-heading font-bold text-white">{supplier.payment_terms || '—'}</div>
        </div>
      </div>

      {id !== 'new' && (
        <div className="flex gap-1 mb-4 border-b border-uf-border">
          {[{ k: 'details', l: 'Details' }, { k: 'products', l: `Products (${products.length})` }, { k: 'pos', l: `Purchase Orders (${pos.length})` }].map(t =>
            <button key={t.k} onClick={() => setTab(t.k)} data-testid={`supplier-tab-${t.k}`}
                    className={`px-4 py-2 text-sm font-medium border-b-2 transition ${tab === t.k ? 'border-uf-red text-white' : 'border-transparent text-uf-textMuted hover:text-white'}`}>{t.l}</button>
          )}
        </div>
      )}

      {tab === 'details' && (
        <div className="bg-uf-surface border border-uf-border rounded p-6 max-w-3xl">
          <div className="flex justify-end mb-4">
            {!editing && id !== 'new' && <PermissionGate perm="purchasing.suppliers"><button onClick={() => setEditing(true)} data-testid="edit-supplier-btn" className="px-3 py-1.5 bg-uf-red text-white rounded text-sm flex items-center gap-1"><Edit3 className="w-3 h-3" /> Edit</button></PermissionGate>}
            {editing && <div className="flex gap-2">
              <button onClick={() => { if (id === 'new') onBack(); else { setEditing(false); setForm(supplier); } }} className="px-3 py-1.5 border border-uf-border text-white rounded text-sm">Cancel</button>
              <button onClick={save} data-testid="save-supplier-btn" className="px-3 py-1.5 bg-uf-red text-white rounded text-sm">Save</button>
            </div>}
          </div>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <F label="Name" value={form.name} edit={editing} onChange={v => setForm({ ...form, name: v })} testid="sup-name" />
            <F label="Account #" value={form.account_number} edit={editing} onChange={v => setForm({ ...form, account_number: v })} testid="sup-account" />
            <F label="Contact name" value={form.contact_name} edit={editing} onChange={v => setForm({ ...form, contact_name: v })} testid="sup-contact" />
            <F label="Email" value={form.email} edit={editing} onChange={v => setForm({ ...form, email: v })} testid="sup-email" />
            <F label="Phone" value={form.phone} edit={editing} onChange={v => setForm({ ...form, phone: v })} testid="sup-phone" />
            <F label="VAT #" value={form.vat_number} edit={editing} onChange={v => setForm({ ...form, vat_number: v })} testid="sup-vat" />
            <F label="Payment terms" value={form.payment_terms || 'Net 30'} edit={editing} onChange={v => setForm({ ...form, payment_terms: v })} options={['Net 30', 'Net 14', 'Net 7', 'COD', 'Prepay']} testid="sup-terms" />
            <F label="Default delivery site" value={form.default_delivery_site_id || ''} edit={editing} onChange={v => setForm({ ...form, default_delivery_site_id: v || null })} options={[['', '— None —'], ...sites.map(s => [s.id, s.name])]} testid="sup-site" />
            <div className="col-span-2"><F label="Address line 1" value={form.address_line_1} edit={editing} onChange={v => setForm({ ...form, address_line_1: v })} testid="sup-addr1" /></div>
            <F label="City" value={form.city} edit={editing} onChange={v => setForm({ ...form, city: v })} testid="sup-city" />
            <F label="Postcode" value={form.postcode} edit={editing} onChange={v => setForm({ ...form, postcode: v })} testid="sup-postcode" />
            <div className="col-span-2"><F label="Notes" value={form.notes} edit={editing} onChange={v => setForm({ ...form, notes: v })} multiline testid="sup-notes" /></div>
          </div>
        </div>
      )}

      {tab === 'products' && (
        <div className="bg-uf-surface border border-uf-border rounded p-6">
          <p className="text-uf-textMuted text-sm mb-3">Products linked to this supplier with their supplier SKU, pack size and cost.</p>
          {products.length === 0 && <div className="text-uf-textMuted text-sm text-center py-6">No products linked yet — link products on the Products card under "Supplier Products" (coming next).</div>}
          {products.map(p =>
            <div key={p.id} className="grid grid-cols-5 gap-2 px-3 py-2 border-b border-uf-border items-center text-sm">
              <div className="text-white col-span-2 flex items-center gap-2"><Package className="w-4 h-4 text-uf-textMuted" />{p.product_name}</div>
              <div className="text-uf-textMuted text-xs font-mono">{p.supplier_sku || '—'}</div>
              <div className="text-uf-textMuted text-sm">Pack × {p.pack_size}</div>
              <div className="text-white text-right">£{p.cost_price?.toFixed(2)}</div>
            </div>
          )}
        </div>
      )}

      {tab === 'pos' && (
        <div className="bg-uf-surface border border-uf-border rounded p-6">
          {pos.length === 0 && <div className="text-uf-textMuted text-sm text-center py-6">No POs with this supplier yet.</div>}
          {pos.map(po =>
            <div key={po.id} onClick={() => window.location.href = `/admin/purchase-orders?open=${po.id}`} data-testid={`supplier-po-${po.id}`}
                 className="grid grid-cols-6 gap-2 px-3 py-2 border-b border-uf-border items-center text-sm cursor-pointer hover:bg-uf-bg">
              <div className="text-white font-mono">{po.po_number}</div>
              <div className="text-uf-textMuted">{po.site_name}</div>
              <div><span className={`text-[10px] px-2 py-0.5 rounded ${po.status === 'received' ? 'bg-green-500/20 text-green-400' : po.status === 'sent' ? 'bg-blue-500/20 text-blue-400' : po.status === 'short_closed' ? 'bg-amber-500/20 text-amber-400' : 'bg-gray-500/20 text-gray-400'}`}>{po.status}</span></div>
              <div className="text-uf-textMuted text-xs">{po.expected_date || '—'}</div>
              <div className="text-white text-right col-span-2">£{po.total_cost?.toFixed(2)}</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function F({ label, value, edit, onChange, options, multiline, testid }) {
  return (
    <div>
      <label className="text-xs text-uf-textMuted block mb-1">{label}</label>
      {!edit ? <div className="text-white">{value ?? '—'}</div>
        : options ? (
          <select value={value || ''} onChange={e => onChange(e.target.value)} data-testid={testid} className="w-full bg-uf-bg border border-uf-border px-3 py-2 text-white rounded">
            {options.map(o => {
              const [v, l] = Array.isArray(o) ? o : [o, o];
              return <option key={v} value={v}>{l}</option>;
            })}
          </select>
        ) : multiline ? (
          <textarea value={value || ''} onChange={e => onChange(e.target.value)} data-testid={testid} rows={3} className="w-full bg-uf-bg border border-uf-border px-3 py-2 text-white rounded" />
        ) : (
          <input value={value || ''} onChange={e => onChange(e.target.value)} data-testid={testid} className="w-full bg-uf-bg border border-uf-border px-3 py-2 text-white rounded" />
        )}
    </div>
  );
}
